// src/encounter/intelligence/next-step-card.tsx
//
// The Next step card and the physician decision buttons, in the encounter drawer's "Scrips Intelligence" section
// beside the Explain entry. The card is code-built by a Scrips rule (no model). Every clinical string is a payload
// field verbatim: item title, evidence, reviewLine, readLine, notCheckedLine and the origin label. The app's own words
// are the fixed button labels and decision lines in next-step.ts. A decision is an audit record only: nothing is
// written to the chart, and the footer says so.
import { useEffect, useId, useState } from 'react';
import { isEncounterFlagOn } from '@/shared/lib/encounter-flags';
import { Button, Icon, Skeleton, TextField } from '@scripsteam/scrips-signal-ds';
import { C } from './explain-card-classes';
import { N } from './next-step-classes';
import { RAIL_WORDS, answerTime } from './rail-words';
import { WORDS } from './explanation';
import { ACTION_LABEL, DECISION_LINE, REASON_MAX, type DecisionAction, type Evidence, type NextStepItem, type NextStepV1 } from './next-step';
import { useNextStep, type DecisionState, type UseNextStepOptions } from './use-next-step';
import { RailMenu } from './rail-menu';

export interface NextStepPanelProps {
  patientId: string;
  encounterId?: string | null;
  fetch?: UseNextStepOptions['fetch'];
  token?: UseNextStepOptions['token'];
  newId?: UseNextStepOptions['newId'];
  /** The decision buttons; default: the `decisionButtons` encounter switch (OFF by default, see encounter-flags.ts). */
  decisionsEnabled?: boolean;
  /**
   * Frame v2 rail (round 18r design): the card loads by itself when the panel mounts (inside the notice gate, so only
   * after the clinician has confirmed the notice), with no "Show" / "Reload" row. Absent: the drawer's "Show" row.
   */
  autoLoad?: boolean;
}

export function NextStepPanel({ patientId, encounterId = null, fetch, token, newId, decisionsEnabled, autoLoad = false }: NextStepPanelProps) {
  const ns = useNextStep({ patientId, encounterId, fetch, token, newId });
  const [buttons] = useState(() => decisionsEnabled ?? isEncounterFlagOn('decisionButtons'));
  const loading = ns.status === 'loading';
  const labelId = `${useId()}-label`;
  const { load } = ns;
  useEffect(() => {
    if (autoLoad) void load();
  }, [autoLoad, load]);
  return (
    <div data-slot="intelligence-next-step" className={C.lines}>
      {autoLoad ? null : (
        <div className={C.footerRow}>
          <span id={labelId}>Next step</span>
          <Button variant="secondary" size="sm" className="text-xs font-normal" aria-describedby={labelId} onClick={() => void ns.load()} disabled={loading}>
            {ns.card || ns.notice ? 'Reload' : 'Show'}
          </Button>
        </div>
      )}
      {loading ? (
        <div role="status" aria-busy="true" aria-label="Loading" data-slot="intelligence-next-step-loading">
          <Skeleton className="h-16" />
        </div>
      ) : null}
      {ns.notice ? (
        <p role="status" className={C.stateLine} data-slot="intelligence-next-step-notice">
          {ns.notice}
        </p>
      ) : null}
      {autoLoad && !loading && (ns.notice || Object.values(ns.decisions).some((d) => d.status === 'conflict')) ? (
        // Codex P2: an automatic load still has a way back: Retry after a failed load, Reload after a decision conflict
        // (whose line asks the clinician to reload the card).
        <Button variant="tertiary" size="sm" className="text-xs font-normal" data-slot="next-step-reload" onClick={() => void ns.load()}>
          {ns.notice ? 'Retry' : 'Reload'}
        </Button>
      ) : null}
      {ns.card ? <NextStepCard card={ns.card} decisions={ns.decisions} onDecide={ns.decide} onRetry={ns.retry} decisionsEnabled={buttons} /> : null}
    </div>
  );
}

export interface NextStepCardProps {
  card: NextStepV1;
  decisions: Record<string, DecisionState>;
  onDecide: (itemId: string, action: DecisionAction, reason?: string) => boolean;
  onRetry: (itemId: string) => boolean;
  decisionsEnabled?: boolean;
}

export function NextStepCard({ card, decisions, onDecide, onRetry, decisionsEnabled = true }: NextStepCardProps) {
  const uid = useId();
  const [open, setOpen] = useState(false);
  const time = answerTime(card.asOf);
  return (
    <section className={C.card} aria-labelledby={`${uid}-title`} data-slot="next-step-card" data-card="proposal">
      <div className={C.titleRow}>
        <h4 id={`${uid}-title`} className={C.title}>
          {RAIL_WORDS.nextStepTitle}
        </h4>
        {time ? (
          <time className={C.meta} dateTime={card.asOf} data-slot="next-step-time">
            {time}
          </time>
        ) : null}
      </div>
      <p className={C.meta} data-slot="next-step-chrome" data-origin={card.origin.kind}>
        {`${card.origin.label} · `}
        <button type="button" className={C.linkButton} aria-expanded={open} aria-controls={open ? `${uid}-how` : undefined} onClick={() => setOpen(!open)}>
          {WORDS.howProduced}
        </button>
      </p>
      {/* N-01: the Read line on its own line, wrapping, never truncated. */}
      <p className={N.readLine} data-slot="next-step-read">
        {card.readLine}
      </p>
      {open ? (
        <div id={`${uid}-how`} className={C.details} data-slot="next-step-how-panel">
          <span>{card.origin.label}</span>
          <time dateTime={card.asOf}>{card.asOf}</time>
          <span>{`Audit ${card.auditId}`}</span>
        </div>
      ) : null}
      {card.items.length ? (
        <ul className={N.items} aria-label="Next steps">
          {card.items.map((item) => (
            <NextStepRow key={item.id} item={item} decision={decisions[item.id]} onDecide={onDecide} onRetry={onRetry} buttons={decisionsEnabled} />
          ))}
        </ul>
      ) : card.emptyLine ? (
        // Engine 0c27ce4: with no items the engine sends its own line; it is drawn verbatim, never an empty card.
        <p className={C.meta} data-slot="next-step-empty">
          {card.emptyLine}
        </p>
      ) : null}
      <hr className={C.hairline} />
      <p className={C.meta} data-slot="next-step-not-checked">
        {card.notCheckedLine}
      </p>
    </section>
  );
}

/** The actions under "More" (round 18r). */
const MORE_ACTIONS: readonly DecisionAction[] = ['defer', 'already-done'];

/** Round 18r "More" (the dots): Defer and Already done, in the rail's DS menu under the button (focus moves into the
 *  menu, arrow keys move, Escape and an outside click close it, focus returns to the dots). */
function MoreActions({ actions, disabled, onPick }: { actions: readonly DecisionAction[]; disabled: boolean; onPick: (a: DecisionAction) => void }) {
  const label = `More: ${actions.map((a) => ACTION_LABEL[a]).join(', ')}`;
  return (
    <RailMenu
      label={label}
      disabled={disabled}
      hostClassName={N.moreHost}
      menuClassName={N.menu}
      itemClassName={N.menuItem}
      items={actions.map((a) => ({ key: a, label: ACTION_LABEL[a], action: a, onPick: () => onPick(a) }))}
      trigger={
        <button type="button" className={N.more} data-action="more" aria-label={label} title={label}>
          <Icon name="more" size={16} aria-hidden />
        </button>
      }
    />
  );
}

function EvidenceLine({ e }: { e: Evidence }) {
  const ids = [e.pmid ? `PMID ${e.pmid}` : null, e.doi ? `DOI ${e.doi}` : null].filter(Boolean).join(' · ');
  return (
    <p className={N.evidence} data-slot="next-step-evidence">
      <a href={e.link} target="_blank" rel="noopener noreferrer" className={N.link}>
        {e.title}
      </a>
      <span>{` ${e.journal} ${e.year}`}</span>
      {ids ? <span>{` · ${ids}`}</span> : null}
    </p>
  );
}

function NextStepRow({ item, decision, onDecide, onRetry, buttons }: { item: NextStepItem; decision: DecisionState | undefined; onDecide: NextStepCardProps['onDecide']; onRetry: NextStepCardProps['onRetry']; buttons: boolean }) {
  const uid = useId();
  const [asking, setAsking] = useState(false);
  const [reason, setReason] = useState('');
  const closed = decision?.status === 'sending' || decision?.status === 'recorded' || decision?.status === 'conflict';
  const press = (a: DecisionAction) => {
    if (a === 'not-indicated') {
      setAsking(true);
      return;
    }
    setAsking(false);
    onDecide(item.id, a);
  };
  return (
    <li className={N.item} data-slot="next-step-item" data-item-id={item.id}>
      <p className={N.itemTitle}>{item.title}</p>
      {item.evidence ? (
        <EvidenceLine e={item.evidence} />
      ) : (
        <p className={C.meta} data-slot="next-step-no-evidence">
          {item.evidenceLine}
        </p>
      )}
      <p className={C.meta} data-slot="next-step-review">
        {item.reviewLine}
      </p>
      {/* Round 18r: "Add to note" (outlined), "Not indicated" (text), and Defer / Already done under "More". With the
          decisionButtons switch off the decisions route is not yet stamped: the same controls, drawn and disabled. */}
      <div className={N.actions} role="group" aria-label={item.title} data-slot="next-step-actions">
        {item.actions.includes('add-to-note') ? (
          <Button variant="secondary" size="sm" className="text-xs font-normal" data-action="add-to-note" disabled={!buttons || closed} onClick={() => press('add-to-note')}>
            {ACTION_LABEL['add-to-note']}
          </Button>
        ) : null}
        {item.actions.includes('not-indicated') ? (
          <Button variant="tertiary" size="sm" className="text-xs font-normal" data-action="not-indicated" disabled={!buttons || closed} onClick={() => press('not-indicated')}>
            {ACTION_LABEL['not-indicated']}
          </Button>
        ) : null}
        <span className="flex-1" />
        {MORE_ACTIONS.some((a) => item.actions.includes(a)) ? (
          <MoreActions actions={MORE_ACTIONS.filter((a) => item.actions.includes(a))} disabled={!buttons || closed} onPick={press} />
        ) : null}
      </div>
      {buttons && asking && !closed ? (
        <form
          className={N.reasonRow}
          onSubmit={(ev) => {
            ev.preventDefault();
            if (onDecide(item.id, 'not-indicated', reason)) setAsking(false);
          }}
        >
          <label htmlFor={`${uid}-reason`} className={C.meta}>
            Reason (required)
          </label>
          <TextField id={`${uid}-reason`} required maxLength={REASON_MAX} value={reason} onChange={(ev) => setReason(ev.target.value)} />
          <Button type="submit" variant="secondary" size="sm" className="text-xs font-normal" disabled={!reason.trim()}>
            Record
          </Button>
        </form>
      ) : null}
      {decision?.status === 'recorded' ? (
        <p role="status" className={C.stateLine} data-slot="next-step-recorded">
          {DECISION_LINE.recorded(decision.request.action)}
        </p>
      ) : null}
      {decision?.line ? (
        <p role="status" className={C.stateLine} data-slot="next-step-decision-line">
          {decision.line}
        </p>
      ) : null}
      {buttons && decision?.status === 'retryable' ? (
        <Button variant="tertiary" size="sm" className="text-xs font-normal" onClick={() => onRetry(item.id)}>
          Try again
        </Button>
      ) : null}
    </li>
  );
}
